import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { partnerService } from "@/services/partnerService";
import { PartnerDto } from "@/types/partner";

interface ReferralInfoCardProps {
  referralCode: string;
}

export default function ReferralInfoCard({
  referralCode,
}: ReferralInfoCardProps) {
  const [partnerInfo, setPartnerInfo] = useState<PartnerDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateReferralCode = async () => {
      if (!referralCode || referralCode.trim() === "") {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const validation = await partnerService.validateReferralCode(
          referralCode.trim(),
        );

        if (validation.valid && validation.partner) {
          setIsValid(true);
          setPartnerInfo(validation.partner);
          setError(null);
        } else {
          setIsValid(false);
          setPartnerInfo(null);
          setError(validation.message || "Invalid referral code");
        }
      } catch (error) {
        console.error("Error validating referral code:", error);
        setIsValid(false);
        setPartnerInfo(null);
        setError("Failed to validate referral code");
      } finally {
        setIsLoading(false);
      }
    };

    validateReferralCode();
  }, [referralCode]);

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-foreground" />
            Referral Information
          </CardTitle>
          <CardDescription>
            Validating referral code: {referralCode}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Validating referral code...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isValid || !partnerInfo) {
    return (
      <Card className="w-full border-destructive/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-destructive" />
            Referral Information
          </CardTitle>
          <CardDescription>Referral code: {referralCode}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{error || "Invalid referral code"}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full border-success/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5 text-foreground" />
          Referral Information
          <Badge
            variant="outline"
            className="ml-auto border border-success/40 bg-success/15 text-success"
          >
            <CheckCircle className="h-3 w-3 mr-1" />
            Valid
          </Badge>
        </CardTitle>
        <CardDescription>This event was referred by a partner</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">
              Company
            </p>
            <p className="text-base font-medium">
              {partnerInfo.companyName || "Not provided"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">
              Contact
            </p>
            <p className="text-base font-medium">{partnerInfo.contactName}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">
              Referral Code
            </p>
            <p className="font-mono px-2 py-1 rounded text-sm">
              {partnerInfo.referralCode}
            </p>
          </div>
        </div>

        {(partnerInfo.email || partnerInfo.phone) && (
          <div className="border-t pt-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {partnerInfo.email && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Email
                  </p>
                  <p className="text-sm">{partnerInfo.email}</p>
                </div>
              )}

              {partnerInfo.phone && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Phone
                  </p>
                  <p className="text-sm">{partnerInfo.phone}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
